import React, { useState, useEffect, lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Sparkles, TrendingUp, Menu, X, CheckCircle, Camera, Download, Settings, CreditCard, History, Plus, BarChart3, MapPin, User, Bell, Search, Zap, Shield, Globe, Users, Key, FileText, Filter, Calendar, Target, Award, Activity, PieChart, TrendingDown, Clock, Star, AlertCircle, CheckCircle2, ScanSearch, Navigation2, ChevronDown, ChevronUp, Home, Building2, LogOut, Crown, Trash2 } from 'lucide-react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { pdf } from '@react-pdf/renderer'

// Route modules
import {
  LandingPageRoute,
  LoginPageRoute,
  ForgotPasswordPageRoute,
  ResetPasswordPageRoute,
  PrivacyPolicyPageRoute,
  TermsConditionsPageRoute,
  AcceptableUsePageRoute,
  PrivateBetaPageRoute
} from './components/routes/PublicRoutes'

import {
  SignupPageRoute,
  AuthCallbackRoute,
  InviteCodePageRoute,
  InviteRequiredPageRoute
} from './components/routes/AuthRoutes'

import {
  ProtectedRoute,
  PaymentPageRoute,
  CreditsPageRoute,
  AccountPageRoute,
  SettingsPageRoute,
  AdminDashboardRoute,
  ListingAnalysisRoute,
  PhotoLocationSearchRoute
} from './components/routes/ProtectedRoutes'

// Components still needed by Dashboard
import PropertyReport from './components/PropertyReport'

// Public Share View
import SharedAnalysisPage from './pages/SharedAnalysisPage';
import SavedListingsPage from './pages/SavedListingsPage';
import DashboardAnalytics from './pages/DashboardAnalytics';
import PropertyComparisonTool from './pages/PropertyComparisonTool';

// Hooks and utilities
import { formatTimeAgo } from './utils/formatters'
import { useAuth } from './context/AuthContext'
import API_BASE_URL from './config/api'

const API_URL = API_BASE_URL;

// Lazy load heavy components for better performance
const Dashboard = lazy(() => import('./components/Dashboard'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
    <div className="text-center">
      <div className="relative w-16 h-16 mx-auto mb-4">
        <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
        <div className="absolute inset-0 border-4 border-t-emerald-600 rounded-full animate-spin"></div>
      </div>
      <p className="text-gray-600 font-medium">Loading...</p>
    </div>
  </div>
);

// Main App component with Routes
function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPageRoute />} />
      <Route path="/invite" element={<InviteCodePageRoute />} />
      <Route path="/beta" element={<PrivateBetaPageRoute />} />
      <Route path="/invite-required" element={<InviteRequiredPageRoute />} />
      <Route path="/login" element={<LoginPageRoute />} />
      <Route path="/forgot-password" element={<ForgotPasswordPageRoute />} />
      <Route path="/reset-password" element={<ResetPasswordPageRoute />} />
      <Route path="/privacy" element={<PrivacyPolicyPageRoute />} />
      <Route path="/terms" element={<TermsConditionsPageRoute />} />
      <Route path="/acceptable-use" element={<AcceptableUsePageRoute />} />
      <Route path="/signup" element={<SignupPageRoute />} />
      <Route path="/auth/callback" element={<AuthCallbackRoute />} />
      <Route path="/share/:shareId" element={<SharedAnalysisPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><Dashboard /></Suspense></ProtectedRoute>} />
      <Route path="/dashboard/payment" element={<ProtectedRoute><PaymentPageRoute /></ProtectedRoute>} />
      <Route path="/dashboard/credits" element={<ProtectedRoute><CreditsPageRoute /></ProtectedRoute>} />
      <Route path="/dashboard/account" element={<ProtectedRoute><AccountPageRoute /></ProtectedRoute>} />
      <Route path="/dashboard/settings" element={<ProtectedRoute><SettingsPageRoute /></ProtectedRoute>} />
      <Route path="/dashboard/listing-analysis" element={<ProtectedRoute><ListingAnalysisRoute /></ProtectedRoute>} />
      <Route path="/dashboard/saved-listings" element={<ProtectedRoute><SavedListingsPage /></ProtectedRoute>} />
      <Route path="/dashboard/analytics" element={<ProtectedRoute><DashboardAnalytics /></ProtectedRoute>} />
      <Route path="/dashboard/comparison" element={<ProtectedRoute><PropertyComparisonTool /></ProtectedRoute>} />
      <Route path="/dashboard/photo-location-search" element={<ProtectedRoute><PhotoLocationSearchRoute /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboardRoute /></ProtectedRoute>} />
    </Routes>
  );
}

export default App;